import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  CostCenterView, DepartmentNode, DepartmentView, DesignationView, DivisionView,
  EstablishmentView, GradeView, LegalEntityView, LocationView, PositionView,
} from '../types'

const BASE = '/v1/org'

export function useDivisions() {
  return useQuery({
    queryKey: ['org', 'divisions'],
    queryFn: async () => (await apiClient.get<DivisionView[]>(`${BASE}/divisions`)).data,
  })
}

export function useEstablishments() {
  return useQuery({
    queryKey: ['org', 'establishments'],
    queryFn: async () => (await apiClient.get<EstablishmentView[]>(`${BASE}/establishments`)).data,
  })
}

/**
 * There is no delete, only deactivate.
 *
 * An assignment from two years ago points at the division somebody was in two years ago;
 * removing the row would leave every historical assignment, attendance report and payroll
 * line referring to something that no longer exists.
 */
export function useOrgAdminMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['org'] })

  return {
    createDivision: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<DivisionView>(`${BASE}/divisions`, body)).data,
      onSuccess: invalidate,
    }),
    updateDivision: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<DivisionView>(`${BASE}/divisions/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    deactivateDivision: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.post(`${BASE}/divisions/${id}/deactivate`)
      },
      onSuccess: invalidate,
    }),
    createEstablishment: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<EstablishmentView>(`${BASE}/establishments`, body)).data,
      onSuccess: invalidate,
    }),
    updateEstablishment: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<EstablishmentView>(`${BASE}/establishments/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    deactivateEstablishment: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.post(`${BASE}/establishments/${id}/deactivate`)
      },
      onSuccess: invalidate,
    }),
  }
}

// --- Phase 7 part 5: departments and the other masters ---

export function useDepartments() {
  return useQuery({
    queryKey: ['org', 'departments'],
    queryFn: async () => (await apiClient.get<DepartmentView[]>(`${BASE}/departments`)).data,
  })
}

export function useDepartmentTree() {
  return useQuery({
    queryKey: ['org', 'departments', 'tree'],
    queryFn: async () => (await apiClient.get<DepartmentNode[]>(`${BASE}/departments/tree`)).data,
  })
}

/**
 * The code a new record of this kind would get.
 *
 * <p>A preview, not a reservation — the server takes nothing from the series, so opening a
 * dialog and cancelling leaves no gap in the numbering. The dialog submits a blank code when
 * this value was left untouched, and the save allocates for real.
 *
 * <p>Not cached: a stale preview would show a number another administrator has since used.
 */
export function useNextCode(purpose: string | null) {
  return useQuery({
    queryKey: ['org', 'next-code', purpose],
    enabled: purpose != null,
    gcTime: 0,
    staleTime: 0,
    queryFn: async () =>
      (await apiClient.get<{ purpose: string; code: string }>(`${BASE}/next-code`, {
        params: { purpose },
      })).data.code,
  })
}

export function useLegalEntities() {
  return useQuery({
    queryKey: ['org', 'legal-entities'],
    queryFn: async () => (await apiClient.get<LegalEntityView[]>(`${BASE}/legal-entities`)).data,
  })
}

export function useCostCenters() {
  return useQuery({
    queryKey: ['org', 'cost-centers'],
    queryFn: async () => (await apiClient.get<CostCenterView[]>(`${BASE}/cost-centers`)).data,
  })
}

export function useGrades() {
  return useQuery({
    queryKey: ['org', 'grades'],
    queryFn: async () => (await apiClient.get<GradeView[]>(`${BASE}/grades`)).data,
  })
}

export function useLocations() {
  return useQuery({
    queryKey: ['org', 'locations'],
    queryFn: async () => (await apiClient.get<LocationView[]>(`${BASE}/locations`)).data,
  })
}

export function useDesignations() {
  return useQuery({
    queryKey: ['org', 'designations'],
    queryFn: async () => (await apiClient.get<DesignationView[]>(`${BASE}/designations`)).data,
  })
}

export function usePositions() {
  return useQuery({
    queryKey: ['org', 'positions'],
    queryFn: async () => (await apiClient.get<PositionView[]>(`${BASE}/positions`)).data,
  })
}

/**
 * The three writes positions never had.
 *
 * <p>The table has been readable since the workforce migration and writable from nowhere, so
 * the only seats that existed came from a migration — which is why the headcount plan on every
 * installation is whatever it was on day one.
 */
export function usePositionMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['org'] })

  return {
    save: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<PositionView>(`${BASE}/positions/${id}`, body)).data
          : (await apiClient.post<PositionView>(`${BASE}/positions`, body)).data,
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.post(`${BASE}/positions/${id}/deactivate`)
      },
      onSuccess: invalidate,
    }),
  }
}

/** Create, edit, deactivate. Never delete — the same rule as divisions, for the same reason. */
export function useOrgMasterMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['org'] })
  const save = <T,>(path: string) => ({
    create: async (body: Record<string, unknown>) =>
      (await apiClient.post<T>(`${BASE}/${path}`, body)).data,
    update: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      (await apiClient.put<T>(`${BASE}/${path}/${id}`, body)).data,
    deactivate: async (id: string) => {
      await apiClient.post(`${BASE}/${path}/${id}/deactivate`)
    },
  })

  const departments = save<DepartmentView>('departments')
  const entities = save<LegalEntityView>('legal-entities')
  const costCenters = save<CostCenterView>('cost-centers')
  const locations = save<LocationView>('locations')
  const grades = save<GradeView>('grades')
  const designations = save<DesignationView>('designations')

  return {
    createDepartment: useMutation({ mutationFn: departments.create, onSuccess: invalidate }),
    updateDepartment: useMutation({ mutationFn: departments.update, onSuccess: invalidate }),
    deactivateDepartment: useMutation({ mutationFn: departments.deactivate, onSuccess: invalidate }),
    createLegalEntity: useMutation({ mutationFn: entities.create, onSuccess: invalidate }),
    updateLegalEntity: useMutation({ mutationFn: entities.update, onSuccess: invalidate }),
    deactivateLegalEntity: useMutation({ mutationFn: entities.deactivate, onSuccess: invalidate }),
    createCostCenter: useMutation({ mutationFn: costCenters.create, onSuccess: invalidate }),
    updateCostCenter: useMutation({ mutationFn: costCenters.update, onSuccess: invalidate }),
    deactivateCostCenter: useMutation({ mutationFn: costCenters.deactivate, onSuccess: invalidate }),
    createLocation: useMutation({ mutationFn: locations.create, onSuccess: invalidate }),
    updateLocation: useMutation({ mutationFn: locations.update, onSuccess: invalidate }),
    deactivateLocation: useMutation({ mutationFn: locations.deactivate, onSuccess: invalidate }),
    createGrade: useMutation({ mutationFn: grades.create, onSuccess: invalidate }),
    updateGrade: useMutation({ mutationFn: grades.update, onSuccess: invalidate }),
    deactivateGrade: useMutation({ mutationFn: grades.deactivate, onSuccess: invalidate }),
    createDesignation: useMutation({ mutationFn: designations.create, onSuccess: invalidate }),
    updateDesignation: useMutation({ mutationFn: designations.update, onSuccess: invalidate }),
    deactivateDesignation: useMutation({ mutationFn: designations.deactivate, onSuccess: invalidate }),
  }
}
